import React, { useState, useEffect, useRef } from 'react';
import { Search, Clock, TrendingUp, X } from 'lucide-react';
import axios from 'axios';
import { secureStorage } from '../utils/secureStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SearchSuggestions - Dropdown showing recent searches and smart suggestions
 * Shows when user focuses on search input
 */
const SearchSuggestions = ({ 
  query, 
  onSelect, 
  isVisible, 
  onClose,
  inputRef 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch suggestions when query changes or dropdown opens
  useEffect(() => {
    if (!isVisible) return;
    
    const abortController = new AbortController();
    
    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const token = secureStorage.getToken();
        if (!token) return;
        
        const response = await axios.get(
          `${API}/search/suggestions?q=${encodeURIComponent(query || '')}&limit=8`,
          { headers: { Authorization: `Bearer ${token}` }, signal: abortController.signal }
        );
        
        if (response.data.success) {
          setSuggestions(response.data.suggestions || []);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error fetching suggestions:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    // Debounce the fetch
    const timer = setTimeout(fetchSuggestions, 300);
    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [query, isVisible]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        inputRef?.current &&
        !inputRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, onClose, inputRef]);

  if (!isVisible || suggestions.length === 0) return null;

  return (
    <div 
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
      data-testid="search-suggestions-dropdown"
    >
      <div className="py-2">
        {suggestions.map((suggestion, index) => (
          <button
            key={`${suggestion.query}-${index}`}
            onClick={() => {
              onSelect(suggestion.query);
              onClose();
            }}
            className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
            data-testid={`suggestion-${index}`}
          >
            {suggestion.type === 'recent' ? (
              <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
            ) : (
              <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0" />
            )}
            <span className="flex-1 text-gray-700 truncate">
              {suggestion.query}
            </span>
            {suggestion.type === 'recent' && (
              <span className="text-xs text-gray-400">Recent</span>
            )}
            {suggestion.type === 'popular' && (
              <span className="text-xs text-green-600">Popular</span>
            )}
          </button>
        ))}
      </div>
      
      {/* Clear history option */}
      <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
        <p className="text-xs text-gray-500">
          💡 Your recent searches help personalize results
        </p>
      </div>
    </div>
  );
};

export default SearchSuggestions;
